import {
  isLoading,
  LOADING,
  MaybeLoading,
  SignedUrl,
} from "@faro-lotv/foundation";
import {
  IElementImageCommand,
  IElementImg2d,
  IElementImg360,
  isIElementImg2d,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import {
  makeIElementUriSigner,
  ProjectApi,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useMemo } from "react";
import { IElementImg } from "./use-placeholders";

/**
 * @param img The panorama image or 2D photo or image command to get a preview of.
 * @returns The signed URL (if available) to a low-resolution preview image for the panorama.
 */
export function useImgPreviewSignedUrl(
  img: MaybeLoading<IElementImg360 | IElementImg2d | IElementImageCommand>,
): MaybeLoading<SignedUrl> {
  const { projectApiClient } = useApiClientContext();

  return useMemo(
    () => getImageSignedUrl(img, projectApiClient),
    [img, projectApiClient],
  );
}

/**
 * @param img The panorama image or 2D photo or image command to get a preview of
 * @param projectApiClient The client to talk to backend API
 * @returns The signed URL (if available) to a low-resolution preview image for the panorama or 2D photo
 */
function getImageSignedUrl(
  img: MaybeLoading<IElementImg | IElementImageCommand>,
  projectApiClient: ProjectApi,
): MaybeLoading<SignedUrl> {
  if (!img) return;
  if (isLoading(img)) return LOADING;

  if (img.thumbnailUri) {
    return new SignedUrl(
      img.thumbnailUri,
      makeIElementUriSigner(projectApiClient, img.id),
      img.signedThumbnailUri && img.signedThumbnailUriExpiresOn
        ? {
            signedUrl: img.signedThumbnailUri,
            expiresOn: new Date(img.signedThumbnailUriExpiresOn),
          }
        : undefined,
    );
  }

  if (isIElementImg2d(img)) {
    return new SignedUrl(
      img.uri,
      makeIElementUriSigner(projectApiClient, img.id),
      img.signedUri && img.signedUriExpiresOn
        ? {
            signedUrl: img.signedUri,
            expiresOn: new Date(img.signedUriExpiresOn),
          }
        : undefined,
    );
  }

  if (isIElementImg360(img) && img.levelsOfDetail) {
    const firstLevel = img.levelsOfDetail.find(
      (lod) => lod.level === 0 && lod.sources.length === 1,
    );

    if (firstLevel) {
      return new SignedUrl(
        firstLevel.sources[0],
        makeIElementUriSigner(projectApiClient, img.id),
        firstLevel.signedSources?.[0] && firstLevel.signedSourcesExpiresOn
          ? {
              signedUrl: firstLevel.signedSources[0],
              expiresOn: new Date(firstLevel.signedSourcesExpiresOn),
            }
          : undefined,
      );
    }
  }
}
